/** @jsxImportSource @emotion/react */
import Toast from 'components/atoms/Toast'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import { Button } from 'react-bootstrap'
import {
  useAddBillingNoteMutation,
  useEditBillingNoteMutation,
  useRemoveBillingNoteMutation,
} from 'store/services/billingNotes'
import { BillingNoteDto, BillingNoteEntityType } from 'store/types'
import { formatEasternDateTime } from 'helpers/dates'

// Mirrors NOTE_MAX_LENGTH in add-billing-note.handler.ts. Enforced here too so
// the limit shows up as the field refusing more text, rather than as a 400
// after the biller has already written past it.
const NOTE_MAX_LENGTH = 1000

// Caps growth at roughly four lines and scrolls past that, so a long note
// cannot push the row's buttons off screen.
const noteInputStyle = {
  fontSize: '0.813rem',
  maxHeight: 100,
  overflowY: 'auto',
} as const

// The backend enum reaches the front end as a type only — importing it as a
// value would pull backend code into the bundle — so the three members are
// re-stated here and asserted once, rather than at every call site.
export const NoteEntity = {
  CHARGE: 'CHARGE' as BillingNoteEntityType,
  PRE_PAYMENT: 'PRE_PAYMENT' as BillingNoteEntityType,
  PAYMENT_TRANSACTION: 'PAYMENT_TRANSACTION' as BillingNoteEntityType,
  NEEDS_WORK: 'NEEDS_WORK' as BillingNoteEntityType,
}

interface Props {
  notes: BillingNoteDto[]
  // What the notes hang off: a charge, a payment, or one payment transaction.
  type: BillingNoteEntityType
  entityId: string
  onChange?: () => void
}

const formatTimestamp = (value: string | Date | null | undefined) => formatEasternDateTime(value, '')

// Notes the platform wrote itself have no author to name.
const authorOf = (note: BillingNoteDto): string => {
  const name = [note.authorFirstName, note.authorLastName].filter(Boolean).join(' ').trim()

  if (name) return name
  if (note.authorEmail) return note.authorEmail

  return 'System'
}

export const BillingNotes: React.FC<Props> = ({ notes, type, entityId, onChange }) => {
  const [draft, setDraft] = React.useState('')
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [editDraft, setEditDraft] = React.useState('')

  const [addNote, { isLoading: isAdding }] = useAddBillingNoteMutation()
  const [editNote, { isLoading: isEditing }] = useEditBillingNoteMutation()
  const [removeNote] = useRemoveBillingNoteMutation()

  const handleAdd = async () => {
    const body = draft.trim()
    if (!body) return

    try {
      await addNote({ type, entityId, body }).unwrap()
      setDraft('')
      onChange?.()
      Toast({ type: 'success', label: 'Note added' })
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to add note' })
    }
  }

  const handleSaveEdit = async (noteId: string) => {
    const body = editDraft.trim()
    if (!body) return

    try {
      await editNote({ noteId, body }).unwrap()
      setEditingId(null)
      setEditDraft('')
      onChange?.()
      Toast({ type: 'success', label: 'Note updated' })
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to update note' })
    }
  }

  const handleRemove = async (note: BillingNoteDto) => {
    try {
      await confirm('', {
        title: 'Delete this note?',
        description: `"${note.body}" — written by ${authorOf(note)}. This removes it from the account history.`,
        yesAction: 'Delete',
      })
    } catch {
      return
    }

    try {
      await removeNote({ noteId: note.id }).unwrap()
      onChange?.()
      Toast({ type: 'success', label: 'Note deleted' })
    } catch (error: any) {
      Toast({ type: 'error', label: error?.data?.message || 'Failed to delete note' })
    }
  }

  return (
    <div css={{ fontSize: '0.813rem' }} onClick={(event) => event.stopPropagation()}>
      {notes.length === 0 && (
        <div css={{ color: '#6c757d', fontStyle: 'italic', marginBottom: '0.5rem' }}>No notes yet</div>
      )}

      {notes.map((note) => (
        <div key={note.id} css={{ padding: '0.4rem 0', borderBottom: '1px solid #e9ecef' }}>
          {editingId === note.id ? (
            <div className="d-flex" css={{ gap: '0.25rem' }}>
              <textarea
                className="form-control form-control-sm"
                rows={2}
                maxLength={NOTE_MAX_LENGTH}
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                css={noteInputStyle}
              />
              <Button
                variant="primary"
                size="sm"
                disabled={isEditing || !editDraft.trim()}
                onClick={() => handleSaveEdit(note.id)}
                css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
                Save
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={isEditing}
                onClick={() => {
                  setEditingId(null)
                  setEditDraft('')
                }}
                css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <div css={{ whiteSpace: 'pre-wrap' }}>{note.body}</div>
              <div className="d-flex align-items-center" css={{ gap: '0.5rem', marginTop: '0.15rem' }}>
                <span css={{ color: '#6c757d', fontSize: '0.75rem' }}>
                  {authorOf(note)} · {formatTimestamp(note.createdAt)}
                  {note.updatedAt !== note.createdAt && ' · edited'}
                </span>
                <Button
                  variant="link"
                  size="sm"
                  css={{ padding: 0, fontSize: '0.75rem' }}
                  onClick={() => {
                    setEditingId(note.id)
                    setEditDraft(note.body)
                  }}>
                  edit
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  css={{ padding: 0, fontSize: '0.75rem', color: '#dc3545' }}
                  onClick={() => handleRemove(note)}>
                  delete
                </Button>
              </div>
            </>
          )}
        </div>
      ))}

      <div className="d-flex" css={{ gap: '0.25rem', marginTop: '0.5rem' }}>
        <textarea
          className="form-control form-control-sm"
          rows={2}
          maxLength={NOTE_MAX_LENGTH}
          placeholder="Add a note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          css={noteInputStyle}
        />
        <Button
          variant="outline-primary"
          size="sm"
          disabled={isAdding || !draft.trim()}
          onClick={handleAdd}
          css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}>
          {isAdding ? 'Adding...' : 'Add'}
        </Button>
      </div>
    </div>
  )
}

interface IndicatorProps {
  count: number
  isOpen: boolean
  onToggle: () => void
  label?: string
}

// The row affordance, shaped like the charge-id and Denied badges the billing
// tables already use: a filled pill carrying the count when notes exist, an
// outlined one inviting the first note when they don't.
export const NotesIndicator: React.FC<IndicatorProps> = ({ count, isOpen, onToggle, label = 'Note' }) => {
  const hasNotes = count > 0

  return (
    <button
      type="button"
      title={hasNotes ? `${count} note${count === 1 ? '' : 's'}` : 'Add a note'}
      onClick={(event) => {
        // These sit inside rows that expand on click.
        event.stopPropagation()
        onToggle()
      }}
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        // Fully rounded rather than the 4px the charge badge uses: this one is
        // a control, and the pill shape says so without needing a border-only
        // treatment that would disappear against the row.
        borderRadius: '999px',
        padding: '0.1rem 0.55rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        appearance: 'none',
        transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        border: `1px solid ${hasNotes ? '#007bff' : '#ced4da'}`,
        backgroundColor: hasNotes ? '#007bff' : 'transparent',
        color: hasNotes ? '#ffffff' : '#6c757d',
        // While its dialog is open the badge stays visibly the active one, so
        // it is clear which row the overlay belongs to.
        boxShadow: isOpen ? '0 0 0 2px rgba(0, 123, 255, 0.35)' : 'none',
        '&:hover': {
          backgroundColor: hasNotes ? '#0069d9' : '#e9ecef',
          borderColor: hasNotes ? '#0069d9' : '#adb5bd',
          color: hasNotes ? '#ffffff' : '#495057',
        },
        '&:focus-visible': {
          outline: 'none',
          boxShadow: '0 0 0 2px rgba(0, 123, 255, 0.5)',
        },
      }}>
      <i className={`${hasNotes ? 'fas' : 'far'} fa-sticky-note`} />
      {hasNotes ? count : label}
    </button>
  )
}
